import { formatDateTableDisplay } from "@/lib/utils/date/date";
import { toCivilYmd } from "@/lib/utils/date/date-only";
import {
  formatAdminScheduledDropLabel,
  hasAdminScheduledFutureDrop,
  isPendingEnrollment,
  isScheduledDropPending,
  isWaitlistEnrollment,
  occupiesClassSpot,
} from "@/lib/utils/enrollment";
import { getEnrollmentStatusDisplayLabel } from "@/lib/utils/enrollment-status-display";
import type { GetEnrollmentResponse } from "@/openapi/generated";
import { EnrollmentStatus } from "@/openapi/generated/models";

export type ClassRosterCurrentGroup = "enrolled" | "awaiting" | "scheduledDrop";

export type ClassRosterCurrentGroups = {
  enrolled: GetEnrollmentResponse[];
  awaiting: GetEnrollmentResponse[];
  scheduledDrop: GetEnrollmentResponse[];
};

export function formatClassEnrollmentStudentName(
  enrollment: GetEnrollmentResponse,
): string {
  const { firstName, lastName } = enrollment.student;
  return `${firstName} ${lastName}`.trim() || "Unknown student";
}

export function formatEnrollmentStatusChangedAt(
  enrollment: Pick<GetEnrollmentResponse, "statusChangedAt">,
): string | null {
  const raw = enrollment.statusChangedAt;
  if (raw == null || raw === "") {
    return null;
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    return formatDateTableDisplay(trimmed.slice(0, 10));
  }

  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    const ymd = toCivilYmd(raw);
    return ymd ? formatDateTableDisplay(ymd) : null;
  }

  return null;
}

/** Hide statusChangedAt when the status badge already shows the same date context. */
export function shouldShowEnrollmentStatusChangedDate(
  enrollment: GetEnrollmentResponse,
  statusLabel: string,
): boolean {
  if (hasAdminScheduledFutureDrop(enrollment)) {
    return false;
  }

  if (statusLabel.startsWith("Withdraws ")) {
    return false;
  }

  return true;
}

export function getClassEnrollmentStatusMeta(
  enrollment: GetEnrollmentResponse,
): { label: string; tone: "success" | "premium" | "info" | "neutral" } {
  if (hasAdminScheduledFutureDrop(enrollment)) {
    const dateLabel = formatAdminScheduledDropLabel(enrollment);
    return {
      label: dateLabel ? `Withdraws ${dateLabel}` : "Withdrawal scheduled",
      tone: "info",
    };
  }

  if (isScheduledDropPending(enrollment)) {
    return { label: "Withdrawal requested", tone: "info" };
  }

  switch (enrollment.status) {
    case EnrollmentStatus.Enrolled:
      return {
        label: getEnrollmentStatusDisplayLabel(enrollment.status),
        tone: "success",
      };
    case EnrollmentStatus.Pending:
      return {
        label: getEnrollmentStatusDisplayLabel(enrollment.status),
        tone: "premium",
      };
    case EnrollmentStatus.Awaiting:
      return {
        label: getEnrollmentStatusDisplayLabel(enrollment.status),
        tone: "info",
      };
    case EnrollmentStatus.ScheduledDrop:
      return {
        label: getEnrollmentStatusDisplayLabel(enrollment.status),
        tone: "info",
      };
    case EnrollmentStatus.Dropped:
    case EnrollmentStatus.Completed:
    case EnrollmentStatus.Cancelled:
    case EnrollmentStatus.Rejected:
      return {
        label: getEnrollmentStatusDisplayLabel(enrollment.status),
        tone: "neutral",
      };
    default:
      return {
        label:
          getEnrollmentStatusDisplayLabel(enrollment.status) ||
          enrollment.status,
        tone: "neutral",
      };
  }
}

/** Live roster rows: Enrolled and scheduled drops. Not waitlist, pending review, or history. */
export function getClassRosterCurrentGroup(
  enrollment: GetEnrollmentResponse,
): ClassRosterCurrentGroup | null {
  if (isWaitlistEnrollment(enrollment)) return null;
  if (isPendingEnrollment(enrollment)) return null;
  if (!occupiesClassSpot(enrollment)) return null;

  if (
    isScheduledDropPending(enrollment) ||
    hasAdminScheduledFutureDrop(enrollment)
  ) {
    return "scheduledDrop";
  }

  return "enrolled";
}

const ROSTER_DEDUPE_RANK: Record<ClassRosterCurrentGroup, number> = {
  enrolled: 0,
  awaiting: 1,
  scheduledDrop: 2,
};

function rosterDedupeRank(enrollment: GetEnrollmentResponse): number {
  const group = getClassRosterCurrentGroup(enrollment);
  if (group == null) return 99;
  return ROSTER_DEDUPE_RANK[group];
}

/**
 * One row per student. Prefers Enrolled over Awaiting / scheduled drop.
 * Equal rank keeps the first row (callers should sort waitlist first).
 */
export function dedupeClassRosterEnrollmentsByStudent(
  enrollments: GetEnrollmentResponse[],
): GetEnrollmentResponse[] {
  const byStudentId = new Map<number, GetEnrollmentResponse>();
  const withoutStudentId: GetEnrollmentResponse[] = [];

  for (const enrollment of enrollments) {
    const studentId = Number(enrollment.student?.studentId);
    if (!Number.isFinite(studentId) || studentId <= 0) {
      withoutStudentId.push(enrollment);
      continue;
    }

    const existing = byStudentId.get(studentId);
    if (
      !existing ||
      rosterDedupeRank(enrollment) < rosterDedupeRank(existing)
    ) {
      byStudentId.set(studentId, enrollment);
    }
  }

  return [...byStudentId.values(), ...withoutStudentId];
}

export function partitionClassRosterCurrentEnrollments(
  enrollments: GetEnrollmentResponse[],
): ClassRosterCurrentGroups {
  const enrolled: GetEnrollmentResponse[] = [];
  const awaiting: GetEnrollmentResponse[] = [];
  const scheduledDrop: GetEnrollmentResponse[] = [];

  for (const enrollment of enrollments) {
    const group = getClassRosterCurrentGroup(enrollment);
    if (group === "enrolled") enrolled.push(enrollment);
    else if (group === "awaiting") awaiting.push(enrollment);
    else if (group === "scheduledDrop") scheduledDrop.push(enrollment);
  }

  enrolled.sort(sortClassEnrollmentsByName);
  awaiting.sort(sortClassEnrollmentsByName);
  scheduledDrop.sort(sortClassEnrollmentsByName);

  return { enrolled, awaiting, scheduledDrop };
}

export function formatClassRosterSummary(counts: {
  enrolled: number;
  awaiting: number;
  scheduledDrop: number;
  waitlist: number;
  hasWaitList: boolean;
}): string {
  const parts = [
    counts.enrolled === 1 ? "1 enrolled" : `${counts.enrolled} enrolled`,
  ];

  if (counts.awaiting > 0) {
    parts.push(
      counts.awaiting === 1 ? "1 awaiting" : `${counts.awaiting} awaiting`,
    );
  }

  if (counts.scheduledDrop > 0) {
    parts.push(
      counts.scheduledDrop === 1
        ? "1 scheduled drop"
        : `${counts.scheduledDrop} scheduled drops`,
    );
  }

  if (counts.hasWaitList) {
    parts.push(
      counts.waitlist === 1
        ? "1 on waitlist"
        : `${counts.waitlist} on waitlist`,
    );
  }

  return parts.join(" · ");
}

export function sortClassEnrollmentsByName(
  a: GetEnrollmentResponse,
  b: GetEnrollmentResponse,
) {
  return formatClassEnrollmentStudentName(a).localeCompare(
    formatClassEnrollmentStudentName(b),
    undefined,
    { sensitivity: "base" },
  );
}

export function sortClassWaitlistEnrollments(
  a: GetEnrollmentResponse,
  b: GetEnrollmentResponse,
) {
  const aTime = a.enrolledAt
    ? new Date(a.enrolledAt).getTime()
    : Number.POSITIVE_INFINITY;
  const bTime = b.enrolledAt
    ? new Date(b.enrolledAt).getTime()
    : Number.POSITIVE_INFINITY;
  if (aTime !== bTime) return aTime - bTime;
  return sortClassEnrollmentsByName(a, b);
}

export const CLASS_ROSTER_COLLAPSE_THRESHOLD = 10;
